import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import CredentialExpiryBanner from "@/components/CredentialExpiryBanner";
import CoachNotesTimeline from "@/components/CoachNotesTimeline";
import CoachSportsEditor from "@/components/CoachSportsEditor";
import {
  ArrowLeft, CalendarDays, Activity, FileText, Sparkles, Save, ShieldCheck, User as UserIcon, Trash2, KeyRound, Pencil, X, Award, Gift, Eye, EyeOff, Coffee
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

function KV({ label, value }) {
  return (
    <div>
      <div className="label-mono mb-0.5">{label}</div>
      <div className="text-white">{value || "—"}</div>
    </div>
  );
}

const ROLE_PERMS = [
  { id: "super_admin", label: "Super admin", color: "#CCFF00", desc: "Owner. Implicitly grants every permission below." },
  { id: "admin", label: "Admin", color: "#FF4500", desc: "Standard admin. Inherits the mid-tier granular permissions." },
  { id: "coach", label: "Coach", color: "#007AFF", desc: "Can publish offerings and rate players they coach." },
  { id: "student", label: "Student", color: "#A3A3A3", desc: "Books sessions, tracks own progress." },
];

export default function AdminUserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [profile, perms] = await Promise.all([
        api.get(`/admin/users/${id}/profile`),
        api.get("/admin/permissions-catalog"),
      ]);
      setData(profile.data);
      setCatalog(perms.data);
      setPending(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load user");
    }
  };

  useEffect(() => { load(); }, [id]);

  if (!data || !catalog) {
    return <div className="label-mono text-[#A3A3A3]">Loading…</div>;
  }

  const u = data.user;
  const current = pending || u.permissions || [];
  const dirty = pending !== null;
  const togglePerm = (p) => {
    const next = current.includes(p) ? current.filter((x) => x !== p) : [...current, p];
    setPending(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${id}/permissions`, { permissions: current });
      toast.success("Permissions updated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-user-detail-page">
      <div className="fade-up">
        <Link to="/admin/users" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider mb-3" data-testid="user-detail-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to users
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>USER · {u.role?.toUpperCase()}</div>
            <h1 className="h-display text-5xl tracking-tighter" data-testid="user-name">{u.name}</h1>
            <p className="text-sm text-[#A3A3A3] mt-1" data-testid="user-email">{u.email}</p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {(data.effective_permissions || []).map((p) => (
                <span key={p} className="label-mono px-2 py-1 border border-[#262626]">{p.toUpperCase()}</span>
              ))}
              {u.coach_status && <span className="label-mono px-2 py-1" style={{ background: "#007AFF", color: "#0A0A0A", fontWeight: 700 }}>COACH · {u.coach_status.toUpperCase()}</span>}
              {u.assessment_status && <span className="label-mono px-2 py-1" style={{ background: "#FF4500", color: "#0A0A0A", fontWeight: 700 }}>ASSESSMENT · {u.assessment_status.toUpperCase()}</span>}
              {u.dual_role_enabled && <span className="label-mono px-2 py-1" style={{ background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }} data-testid="dual-role-badge">DUAL-ROLE</span>}
            </div>
          </div>
          <div className="text-right space-y-2">
            <div>
              <div className="label-mono">CREATED</div>
              <div className="text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
            </div>
            {/* Dual-role toggle — only for coach/admin accounts */}
            {(u.role === "coach" || u.role === "admin" || (data.effective_permissions || []).some((p) => ["coach","admin","super_admin"].includes(p))) && (
              <button
                type="button"
                data-testid="toggle-dual-role"
                onClick={async () => {
                  const nextOn = !u.dual_role_enabled;
                  const ok = window.confirm(
                    nextOn
                      ? `Enable student profile for ${u.name}?\n\nThey'll be able to switch to a STUDENT view in the sidebar to book sessions, self-rate, and track their own progress alongside coaching.`
                      : `Disable student profile for ${u.name}?\n\nThe mode switcher will disappear from their sidebar. Their student-side data (self-rating, bookings) will be preserved.`
                  );
                  if (!ok) return;
                  try {
                    await api.post(`/admin/users/${id}/dual-role`, { enabled: nextOn });
                    toast.success(nextOn ? "Student profile enabled" : "Student profile disabled");
                    await load();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
                }}
                className="px-4 py-2 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border transition-colors"
                style={u.dual_role_enabled
                  ? { borderColor: "#525252", color: "#A3A3A3" }
                  : { borderColor: "#CCFF00", color: "#CCFF00" }}
              >
                {u.dual_role_enabled ? "Disable student profile" : "+ Enable student profile"}
              </button>
            )}
            {/* Iter102 — Global Access toggle. Only rendered for coach/admin
                accounts. Grants Discovery mode across the whole player roster. */}
            {(u.role === "coach" || u.role === "admin") && (
              <button
                type="button"
                data-testid="toggle-global-access"
                onClick={async () => {
                  const nextOn = !u.has_global_access;
                  const ok = window.confirm(
                    nextOn
                      ? `Grant Global Access to ${u.name}?\n\nThey'll be able to see every player on the roster. Restricted players will still be masked until you or an approver grant explicit access.`
                      : `Revoke Global Access from ${u.name}?\n\nThey'll go back to seeing only players they have a team-share relationship with.`
                  );
                  if (!ok) return;
                  try {
                    await api.put(`/admin/users/${id}/global-access`, { enabled: nextOn });
                    toast.success(nextOn ? "Global Access granted" : "Global Access revoked");
                    await load();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
                }}
                className="px-4 py-2 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border transition-colors"
                style={u.has_global_access
                  ? { borderColor: "#CCFF00", color: "#CCFF00", background: "rgba(204,255,0,0.05)" }
                  : { borderColor: "#525252", color: "#A3A3A3" }}
              >
                {u.has_global_access ? "Global Access ON" : "+ Grant Global Access"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Iter145 — Admin editor for coach-assigned sports (Squash / Pickleball).
          Only relevant for coach/admin accounts. Slots above the permissions
          card so admins configure the sport gate first, then role tier. */}
      {(u.role === "coach" || u.role === "admin") && (
        <section className="fade-up d1" data-testid="coach-sports-section">
          <CoachSportsEditor
            userId={id}
            initial={u.coach_sports || []}
            onSaved={(next) => setData((d) => ({ ...d, user: { ...d.user, coach_sports: next } }))}
          />
        </section>
      )}

      {/* Permissions editor */}
      <section className="card-surface p-6 fade-up d1">        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mono mb-1" style={{ color: "#CCFF00" }}><ShieldCheck className="w-3.5 h-3.5 inline mr-1" /> ROLE & PERMISSIONS</div>
            <h2 className="h-display text-2xl">In-app access</h2>
          </div>
          {dirty && (
            <button onClick={save} disabled={saving} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="save-permissions">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
            </button>
          )}
        </div>
        <p className="text-xs text-[#A3A3A3] mb-4">Toggle the checkboxes to grant or revoke. <span className="text-white">Super admin</span> automatically includes every granular permission; <span className="text-white">Admin</span> grants the mid-tier set. Ala-carte granular permissions can be added without making someone a full admin — perfect for delegating just application approvals.</p>

        <div className="space-y-5">
          <div>
            <div className="label-mono mb-2">ROLE TIER</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {ROLE_PERMS.map((p) => {
                const on = current.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => togglePerm(p.id)}
                    data-testid={`perm-${p.id}`}
                    className="flex items-start gap-3 text-left p-3 border transition-colors"
                    style={on ? { borderColor: p.color, background: "rgba(204,255,0,0.04)" } : { borderColor: "#262626" }}>
                    <div className="w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center border"
                         style={on ? { background: p.color, borderColor: p.color } : { borderColor: "#262626" }}>
                      {on && <svg width="12" height="12" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke="#0A0A0A" strokeWidth="3" fill="none"/></svg>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm" style={{ color: on ? p.color : "white" }}>{p.label}</div>
                      <div className="text-xs text-[#A3A3A3]">{p.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">GRANULAR PERMISSIONS (ALA-CARTE)</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {catalog.granular.map((p) => {
                const meta = catalog.meta[p] || { label: p, desc: "", color: "#CCFF00" };
                const on = current.includes(p);
                const impliedBySuper = current.includes("super_admin");
                const impliedByAdmin = current.includes("admin") && (catalog.implies.admin || []).includes(p);
                const implied = impliedBySuper || impliedByAdmin;
                return (
                  <button key={p} type="button" onClick={() => togglePerm(p)}
                    data-testid={`perm-${p}`}
                    className="flex items-start gap-3 text-left p-3 border transition-colors"
                    style={(on || implied) ? { borderColor: meta.color, background: "rgba(204,255,0,0.04)" } : { borderColor: "#262626" }}>
                    <div className="w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center border"
                         style={(on || implied) ? { background: meta.color, borderColor: meta.color } : { borderColor: "#262626" }}>
                      {(on || implied) && <svg width="12" height="12" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke="#0A0A0A" strokeWidth="3" fill="none"/></svg>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm flex items-center gap-2" style={{ color: (on || implied) ? meta.color : "white" }}>
                        {meta.label}
                        {implied && !on && <span className="label-mono px-1 py-0.5 text-[8px]" style={{ background: meta.color, color: "#0A0A0A" }}>IMPLIED</span>}
                      </div>
                      <div className="text-xs text-[#A3A3A3]">{meta.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Personal details (admin-only view + edit) */}
      <PersonalDetailsCard u={u} reload={load} />

      {/* Coach-facing display name override + note — surfaces on roster / player
          detail views for admins + coaches who have booked with this player.
          Purpose-built for disambiguating two "Rob Smith"s.  */}
      {u.role === "student" && <CoachFacingNoteCard u={u} reload={load} />}

      {/* Coach journal — append-only observations tagged with the player's
          assessment level at the time. Admins can add + read; coaches booked
          with this player also see it via /students/{id}/coach-notes. */}
      {u.role === "student" && <CoachNotesTimeline studentId={u.id} defaultContext="general" />}

      {/* Coach credentials (insurance + certification) — only for coach/admin accounts */}
      {u.role !== "student" && (
        <>
          <CredentialExpiryBanner userId={u.id} refreshKey={u.coach_application?.last_edited_by_admin_at || ""} />
          <CoachCredentialsCard u={u} reload={load} />
        </>
      )}

      {/* Bookings */}
      <section className="card-surface p-6 fade-up d2" data-testid="user-bookings-section">
        <div className="label-mono mb-1"><CalendarDays className="w-3.5 h-3.5 inline mr-1" /> BOOKINGS</div>
        <h2 className="h-display text-2xl mb-4">{data.bookings.length} session{data.bookings.length === 1 ? "" : "s"}</h2>
        {!data.bookings.length ? (
          <div className="text-xs text-[#A3A3A3]">No bookings yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#A3A3A3] text-xs uppercase tracking-wider border-b border-[#262626]">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Sport</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Paid</th>
                  <th className="py-2 pr-3 text-right">Price</th>
                </tr>
              </thead>
              <tbody>
                {data.bookings.map((b) => (
                  <tr key={b.id} className="border-b border-[#262626] last:border-0">
                    <td className="py-2 pr-3">{new Date(b.start).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-2 pr-3 capitalize">{b.sport}</td>
                    <td className="py-2 pr-3 uppercase text-xs">{b.session_type?.replace("_", " ")}{b.is_assessment && <span className="ml-1 label-mono text-[#FF4500]">· ASSESS</span>}</td>
                    <td className="py-2 pr-3 uppercase text-xs">{b.status}</td>
                    <td className="py-2 pr-3 uppercase text-xs" style={{ color: b.payment_status === "paid" ? "#CCFF00" : "#A3A3A3" }}>{b.payment_status || "—"}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap" data-testid={`user-booking-price-${b.id}`}>
                      {b.gifted_credit ? (
                        <>
                          <span className="line-through text-[#737373]">£{b.price || 0}</span>
                          <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 border border-[#FF9F1C]/60 text-[#FF9F1C]" title="Paid with a gifted or coach-given credit — no money changed hands" data-testid={`user-booking-gifted-${b.id}`}>Gifted credit</span>
                        </>
                      ) : `£${b.price || 0}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Iter107 — Gift Credits. Admin-only, no Stripe. Always visible on
          student pages so support can grant credits even when the student
          has never bought a pack. */}
      {u.role === "student" && (
        <GiftCreditsSection userId={id} userName={u.name} onGranted={load} />
      )}

      {/* Iter108b — Admin-side coach visibility & Away Mode. Only shown on
          coach/admin profiles so support can hide bad actors or toggle Away
          when the coach can't log in themselves. */}
      {(u.role === "coach" || u.role === "admin") && (
        <CoachVisibilityAdminSection user={u} onChanged={load} />
      )}

      {/* Iter94 — Credit packs (Store-bought). Admin can extend expiry or
          adjust remaining sessions. Only visible when there's at least one. */}
      {u.role === "student" && Array.isArray(data.credit_packs) && data.credit_packs.length > 0 && (
        <CreditPacksSection userId={id} packs={data.credit_packs} onChanged={load} />
      )}

      {/* AI Reports */}
      <section className="card-surface p-6 fade-up d3" data-testid="user-reports-section">
        <div className="label-mono mb-1"><Sparkles className="w-3.5 h-3.5 inline mr-1" /> AI REPORTS</div>
        <h2 className="h-display text-2xl mb-4">{data.reports.length} report{data.reports.length === 1 ? "" : "s"}</h2>
        {!data.reports.length ? (
          <div className="text-xs text-[#A3A3A3]">No AI reports yet. {u.role === "student" ? "Reports are generated by coaches every ~4 weeks." : "This coach hasn't authored any AI reports yet."}</div>
        ) : (
          <div className="space-y-3">
            {data.reports.map((r) => (
              <div key={r.id} className="border border-[#262626] p-3" data-testid={`report-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs text-[#A3A3A3]">{new Date(r.created_at).toLocaleDateString()} · <span className="capitalize">{r.sport}</span></div>
                  {r.window_days && <div className="label-mono">{r.window_days}-DAY WINDOW</div>}
                </div>
                <div className="text-sm whitespace-pre-wrap text-[#E5E5E5]">{(r.summary || r.body || "").slice(0, 400)}{(r.summary || r.body || "").length > 400 ? "…" : ""}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Coach-only: offerings */}
      {u.role !== "student" && data.offerings && (
        <section className="card-surface p-6 fade-up d4" data-testid="user-offerings-section">
          <div className="label-mono mb-1"><Activity className="w-3.5 h-3.5 inline mr-1" /> OFFERINGS</div>
          <h2 className="h-display text-2xl mb-4">{data.offerings.length} offering{data.offerings.length === 1 ? "" : "s"} published</h2>
          {!data.offerings.length ? (
            <div className="text-xs text-[#A3A3A3]">No offerings published yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-2">
              {data.offerings.map((o) => (
                <div key={o.id} className="border border-[#262626] p-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="label-mono">{o.sport?.toUpperCase()} · {o.session_type?.replace("_", " ")}</div>
                    <div className="h-display text-xl">£{o.price_per_player || 0}</div>
                  </div>
                  <div className="font-semibold">{o.title}</div>
                  <div className="text-xs text-[#A3A3A3]">{new Date(o.start).toLocaleString()} · {o.venue}</div>
                  <div className="text-xs text-[#A3A3A3]">{o.enrolled_student_ids?.length || 0} / {o.max_spaces} enrolled</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Skill ratings (small recap) */}
      {data.skill_ratings && data.skill_ratings.length > 0 && (
        <section className="card-surface p-6 fade-up d5" data-testid="user-ratings-section">
          <div className="label-mono mb-1"><FileText className="w-3.5 h-3.5 inline mr-1" /> SKILL RATINGS HISTORY</div>
          <h2 className="h-display text-2xl mb-4">{data.skill_ratings.length} rating entr{data.skill_ratings.length === 1 ? "y" : "ies"}</h2>
          <div className="text-xs text-[#A3A3A3]">Latest: {new Date(data.skill_ratings[0].created_at).toLocaleString()} {data.skill_ratings[0].note ? "· " + data.skill_ratings[0].note : ""}</div>
        </section>
      )}

      {/* Account access — reset / delete (admin-only). Self-protected: cannot reset or delete own row. */}
      {(() => {
        const myPerms = me?.permissions || [];
        const canManage = myPerms.includes("super_admin") || myPerms.includes("manage_users");
        if (!canManage || u.id === me?.id) return null;
        return (
          <section className="card-surface p-6 fade-up" data-testid="user-account-actions">
            <div className="label-mono mb-1" style={{ color: "#CCFF00" }}>
              <KeyRound className="w-3.5 h-3.5 inline mr-1" /> ACCOUNT ACCESS
            </div>
            <h2 className="h-display text-2xl mb-2">Reset their password</h2>
            <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-2xl mb-4">
              Force-set a new password for <span className="text-white font-semibold">{u.name}</span> ({u.email}). Use this if they've lost access to their email or the self-service reset isn't working. The user can change it again after logging in. This action is audit-logged.
            </p>
            <button
              type="button"
              data-testid="reset-password-btn"
              onClick={async () => {
                const pw = window.prompt(
                  `Set a temporary password for ${u.name}.\n\nMust be at least 8 characters. Share it with them through a secure channel — they should change it on next login.`
                );
                if (pw === null) return;
                if ((pw || "").length < 8) { toast.error("Password must be at least 8 characters"); return; }
                const forceChange = window.confirm(
                  `Force ${u.name} to set their own password on next login?\n\n• OK = Yes (recommended). They'll be sent straight to the password-change screen and can't access anything else until they pick a new password.\n• Cancel = No. They can keep using the temp password indefinitely.`
                );
                try {
                  await api.post(`/admin/users/${u.id}/reset-password`, { new_password: pw, force_change: forceChange });
                  toast.success(`Password reset for ${u.email}${forceChange ? " · they'll be forced to change on next login" : ""}`);
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
                }
              }}
              className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors"
            >
              <KeyRound className="w-4 h-4" /> Reset password
            </button>
          </section>
        );
      })()}

      {/* Danger zone — delete user permanently (super-admin only, can't self-delete) */}
      {(() => {
        const myPerms = me?.permissions || [];
        const canDelete = myPerms.includes("super_admin") || myPerms.includes("manage_users");
        if (!canDelete || u.id === me?.id) return null;
        return (
        <section className="card-surface p-6 border-l-4 border-[#FF4500] fade-up" data-testid="user-danger-zone">
          <div className="flex items-start gap-3 mb-4">
            <Trash2 className="w-5 h-5 text-[#FF4500] mt-0.5 shrink-0" />
            <div>
              <h3 className="h-display text-2xl mb-1">Delete this user</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-2xl">
                Permanently removes <span className="text-white font-semibold">{u.name}</span> ({u.email}) and cascades to their offerings, bookings, skill ratings, requests, availability, coach documents and reset tokens. This cannot be undone. Action is recorded in the audit log.
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={deleting}
            data-testid="delete-user-btn"
            onClick={async () => {
              const a = window.prompt(
                `Delete ${u.name} (${u.email}) permanently?\n\nAll their offerings, bookings, ratings, and personal data will be removed. This is irreversible.\n\nType DELETE to confirm:`
              );
              if (a !== "DELETE") { toast.message("Cancelled"); return; }
              setDeleting(true);
              try {
                const { data: res } = await api.delete(`/admin/users/${u.id}`);
                const total = Object.values(res.cascade_counts || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
                toast.success(`Deleted ${u.name} · ${total} related records removed`);
                nav("/admin/users");
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
              } finally { setDeleting(false); }
            }}
            className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" /> {deleting ? "Deleting…" : "Delete user permanently"}
          </button>
        </section>
        );
      })()}
    </div>
  );
}


// --- Editable sub-cards ---

function EditField({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <div className="label-mono mb-1.5">{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        data-testid={testid}
        className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
      />
    </label>
  );
}

function PersonalDetailsCard({ u, reload }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const pd = u.personal_details || {};
  const hasAny = pd && Object.keys(pd).length > 0;

  const startEdit = () => {
    setForm({
      first_name: pd.first_name || (pd.full_name || u.name || "").trim().split(/\s+/).slice(0, -1).join(" ") || "",
      surname: pd.surname || (pd.full_name || u.name || "").trim().split(/\s+/).slice(-1).join(" ") || "",
      full_name: pd.full_name || u.name || "",
      dob: pd.dob || "",
      sex: pd.sex || "",
      phone: pd.phone || "",
      address_line1: pd.address_line1 || "",
      address_line2: pd.address_line2 || "",
      city: pd.city || "",
      postcode: pd.postcode || "",
      country: pd.country || "",
      guardian_name: pd.guardian_name || "",
      guardian_phone: pd.guardian_phone || "",
      guardian_email: pd.guardian_email || "",
    });
    setEditing(true);
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      // Send only the keys that differ from current to keep audit log meaningful.
      const diff = {};
      Object.entries(form).forEach(([k, v]) => {
        if ((pd[k] || "") !== (v || "")) diff[k] = v;
      });
      if (!Object.keys(diff).length) {
        toast.message("No changes to save");
        setEditing(false);
        return;
      }
      await api.put(`/admin/users/${u.id}/personal-details`, diff);
      toast.success("Personal details updated");
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (!editing && !hasAny) {
    return (
      <section className="card-surface p-6 fade-up d2" data-testid="user-personal-section">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="label-mono mb-1"><UserIcon className="w-3.5 h-3.5 inline mr-1" /> PERSONAL DETAILS</div>
            <h2 className="h-display text-2xl">No info captured</h2>
          </div>
          <button onClick={startEdit} data-testid="pd-edit-empty" className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Add details
          </button>
        </div>
        <p className="text-xs text-[#A3A3A3]">This user hasn't completed their personal details. You can add them manually on their behalf.</p>
      </section>
    );
  }

  return (
    <section className="card-surface p-6 fade-up d2" data-testid="user-personal-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-mono mb-1"><UserIcon className="w-3.5 h-3.5 inline mr-1" /> PERSONAL DETAILS</div>
          <h2 className="h-display text-2xl">Captured info {u.profile_completed && <span className="label-mono text-[#CCFF00]">· COMPLETE</span>}</h2>
        </div>
        {!editing ? (
          <button onClick={startEdit} data-testid="pd-edit" className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(false)} data-testid="pd-cancel" className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors inline-flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button onClick={submit} disabled={saving} data-testid="pd-save" className="btn-volt px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <KV label="Full name" value={pd.full_name} />
          <KV label="DOB" value={pd.dob} />
          <KV label="Sex" value={pd.sex?.replace("_", " ")} />
          <KV label="Phone" value={pd.phone} />
          <KV label="Address" value={[pd.address_line1, pd.address_line2].filter(Boolean).join(", ")} />
          <KV label="City" value={pd.city} />
          <KV label="Postcode" value={pd.postcode} />
          <KV label="Country" value={pd.country} />
          {pd.guardian_name && <KV label="Guardian name" value={pd.guardian_name} />}
          {pd.guardian_phone && <KV label="Guardian phone" value={pd.guardian_phone} />}
          {pd.guardian_email && <KV label="Guardian email" value={pd.guardian_email} />}
          <KV label="GDPR consent at" value={pd.gdpr_consent_at ? new Date(pd.gdpr_consent_at).toLocaleString() : "—"} />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <EditField label="First name" value={form.first_name} onChange={set("first_name")} testid="pd-input-first_name" />
          <EditField label="Surname" value={form.surname} onChange={set("surname")} testid="pd-input-surname" />
          <EditField label="DOB (YYYY-MM-DD)" value={form.dob} onChange={set("dob")} type="date" testid="pd-input-dob" />
          <label className="block">
            <div className="label-mono mb-1.5">Sex</div>
            <select value={form.sex || ""} onChange={(e) => set("sex")(e.target.value)} data-testid="pd-input-sex"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]">
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non_binary">Non-binary</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </label>
          <EditField label="Phone" value={form.phone} onChange={set("phone")} testid="pd-input-phone" />
          <EditField label="Address line 1" value={form.address_line1} onChange={set("address_line1")} testid="pd-input-address1" />
          <EditField label="Address line 2" value={form.address_line2} onChange={set("address_line2")} testid="pd-input-address2" />
          <EditField label="City" value={form.city} onChange={set("city")} testid="pd-input-city" />
          <EditField label="Postcode" value={form.postcode} onChange={set("postcode")} testid="pd-input-postcode" />
          <EditField label="Country" value={form.country} onChange={set("country")} testid="pd-input-country" />
          <EditField label="Guardian name" value={form.guardian_name} onChange={set("guardian_name")} testid="pd-input-guardian_name" />
          <EditField label="Guardian phone" value={form.guardian_phone} onChange={set("guardian_phone")} testid="pd-input-guardian_phone" />
          <EditField label="Guardian email" value={form.guardian_email} onChange={set("guardian_email")} type="email" testid="pd-input-guardian_email" />
        </div>
      )}
    </section>
  );
}

function CoachCredentialsCard({ u, reload }) {
  const { user: me } = useAuth();
  const isSuperAdmin = (me?.permissions || []).includes("super_admin");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ enabled: false, reason: "", expires_at: "" });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const app = u.coach_application || {};
  const override = app.credentials_override || {};
  const overrideActive = !!override.enabled && (
    !override.expires_at || override.expires_at >= new Date().toISOString().slice(0, 10)
  );

  const startEdit = () => {
    setForm({
      certification: app.certification || "",
      cert_body: app.cert_body || "",
      cert_expiry: app.cert_expiry || "",
      insurance_provider: app.insurance_provider || "",
      insurance_policy_number: app.insurance_policy_number || "",
      insurance_expiry: app.insurance_expiry || "",
      bio: app.bio || "",
      years_experience: app.years_experience ?? "",
    });
    setEditing(true);
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const diff = {};
      Object.entries(form).forEach(([k, v]) => {
        const old = app[k] ?? "";
        const next = v ?? "";
        if (String(old) !== String(next)) diff[k] = v;
      });
      if (!Object.keys(diff).length) {
        toast.message("No changes to save");
        setEditing(false);
        return;
      }
      await api.put(`/admin/users/${u.id}/coach-application`, diff);
      toast.success("Coach credentials updated");
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const startOverrideEdit = () => {
    setOverrideForm({
      enabled: !!override.enabled,
      reason: override.reason || "",
      expires_at: override.expires_at || "",
    });
    setOverrideOpen(true);
  };
  const saveOverride = async (enabledOverride) => {
    setOverrideSaving(true);
    try {
      const payload = {
        enabled: enabledOverride !== undefined ? enabledOverride : overrideForm.enabled,
        reason: overrideForm.reason,
        expires_at: overrideForm.expires_at,
      };
      await api.put(`/admin/users/${u.id}/credentials-override`, payload);
      toast.success(payload.enabled ? "Override active — coach can keep accepting bookings" : "Override removed");
      setOverrideOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update override");
    } finally { setOverrideSaving(false); }
  };

  const hasAny = app && Object.keys(app).length > 0;

  return (
    <section className="card-surface p-6 fade-up d2" data-testid="user-credentials-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-mono mb-1"><Award className="w-3.5 h-3.5 inline mr-1" /> COACH CREDENTIALS</div>
          <h2 className="h-display text-2xl">Insurance &amp; certification</h2>
          {!hasAny && <p className="text-xs text-[#A3A3A3] mt-1">Coach hasn't submitted an application yet — you can fill it in on their behalf.</p>}
        </div>
        {!editing ? (
          <button onClick={startEdit} data-testid="cred-edit" className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(false)} data-testid="cred-cancel" className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors inline-flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button onClick={submit} disabled={saving} data-testid="cred-save" className="btn-volt px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <KV label="Certification" value={app.certification} />
          <KV label="Awarding body" value={app.cert_body} />
          <KV label="Cert expiry" value={app.cert_expiry} />
          <KV label="Years experience" value={app.years_experience != null ? String(app.years_experience) : ""} />
          <KV label="Insurance provider" value={app.insurance_provider} />
          <KV label="Policy number" value={app.insurance_policy_number} />
          <KV label="Insurance expiry" value={app.insurance_expiry} />
          <KV label="Last edited by admin" value={app.last_edited_by_admin_at ? new Date(app.last_edited_by_admin_at).toLocaleString() : "—"} />
          {app.bio && <div className="sm:col-span-2"><KV label="Bio" value={app.bio} /></div>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <EditField label="Certification name" value={form.certification} onChange={set("certification")} testid="cred-input-cert" placeholder="e.g. IPTPA Level 2" />
          <EditField label="Awarding body" value={form.cert_body} onChange={set("cert_body")} testid="cred-input-cert-body" />
          <EditField label="Cert expiry (YYYY-MM-DD)" type="date" value={form.cert_expiry} onChange={set("cert_expiry")} testid="cred-input-cert-expiry" />
          <EditField label="Years experience" type="number" value={form.years_experience} onChange={set("years_experience")} testid="cred-input-years" />
          <EditField label="Insurance provider" value={form.insurance_provider} onChange={set("insurance_provider")} testid="cred-input-ins-provider" />
          <EditField label="Policy number" value={form.insurance_policy_number} onChange={set("insurance_policy_number")} testid="cred-input-ins-policy" />
          <EditField label="Insurance expiry (YYYY-MM-DD)" type="date" value={form.insurance_expiry} onChange={set("insurance_expiry")} testid="cred-input-ins-expiry" />
          <label className="block sm:col-span-2">
            <div className="label-mono mb-1.5">Bio</div>
            <textarea value={form.bio || ""} onChange={(e) => set("bio")(e.target.value)} rows={3} data-testid="cred-input-bio"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00] resize-none" />
          </label>
        </div>
      )}

      {/* Super-admin override — allow this coach to keep accepting bookings even with lapsed credentials */}
      {isSuperAdmin && !editing && (
        <div className="mt-6 pt-6 border-t border-[#262626]" data-testid="cred-override-section">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="label-mono mb-1" style={{ color: overrideActive ? "#CCFF00" : "#A3A3A3" }}>
                SUPER-ADMIN OVERRIDE {overrideActive && "· ACTIVE"}
              </div>
              <div className="text-sm text-[#E5E5E5] max-w-xl leading-relaxed">
                {overrideActive
                  ? <>This coach can <span className="text-[#CCFF00] font-semibold">still accept bookings</span> while their credentials are lapsed. Granted by {override.granted_by_email || "an admin"} on {override.granted_at ? new Date(override.granted_at).toLocaleDateString() : "—"}{override.expires_at ? `, expires ${override.expires_at}` : " (no expiry)"}.</>
                  : <>Use this when a coach&apos;s insurance/cert is briefly in renewal. While active, students can book them despite the expiry block.</>}
              </div>
              {overrideActive && override.reason && (
                <div className="text-xs text-[#A3A3A3] mt-1 italic">&ldquo;{override.reason}&rdquo;</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {overrideActive ? (
                <button
                  type="button"
                  data-testid="cred-override-revoke"
                  disabled={overrideSaving}
                  onClick={() => saveOverride(false)}
                  className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors disabled:opacity-50"
                >
                  Revoke override
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="cred-override-toggle"
                  onClick={startOverrideEdit}
                  className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors"
                >
                  Grant override
                </button>
              )}
            </div>
          </div>

          {overrideOpen && !overrideActive && (
            <div className="mt-3 p-4 border border-[#CCFF00] bg-[#0a1a05]" data-testid="cred-override-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block sm:col-span-2">
                  <div className="label-mono mb-1.5">Reason (visible in audit log)</div>
                  <input type="text" value={overrideForm.reason}
                    onChange={(e) => setOverrideForm((f) => ({ ...f, reason: e.target.value }))}
                    data-testid="cred-override-reason" placeholder="e.g. Insurance renewal in progress — paperwork due Friday"
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
                </label>
                <label className="block">
                  <div className="label-mono mb-1.5">Expires (optional)</div>
                  <input type="date" value={overrideForm.expires_at}
                    onChange={(e) => setOverrideForm((f) => ({ ...f, expires_at: e.target.value }))}
                    data-testid="cred-override-expires"
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
                </label>
                <div className="flex items-end gap-2">
                  <button type="button" onClick={() => setOverrideOpen(false)}
                    className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors">
                    Cancel
                  </button>
                  <button type="button" disabled={overrideSaving}
                    onClick={() => saveOverride(true)} data-testid="cred-override-save"
                    className="btn-volt px-3 py-2 text-xs disabled:opacity-50">
                    {overrideSaving ? "Saving…" : "Activate override"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function CoachFacingNoteCard({ u, reload }) {
  const [editing, setEditing] = useState(false);
  const [override, setOverride] = useState(u.display_name_override || "");
  const [note, setNote] = useState(u.coach_note || "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setOverride(u.display_name_override || "");
    setNote(u.coach_note || "");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${u.id}/coach-facing-note`, {
        // Empty string clears the field; unchanged fields are omitted so audit
        // logs don't record no-ops.
        display_name_override: override.trim() !== (u.display_name_override || "") ? override.trim() : undefined,
        coach_note: note.trim() !== (u.coach_note || "") ? note.trim() : undefined,
      });
      toast.success("Coach-facing note saved");
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const hasAny = !!(u.display_name_override || u.coach_note);

  return (
    <section className="card-surface p-6 fade-up d2" data-testid="user-coach-note-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-mono mb-1"><UserIcon className="w-3.5 h-3.5 inline mr-1" /> COACH-FACING · DUPLICATE NAME HELPER</div>
          <h2 className="h-display text-2xl">{hasAny ? "Override + note set" : "No override / note"}</h2>
          <p className="text-xs text-[#A3A3A3] mt-1 max-w-2xl leading-relaxed">
            Only admins + coaches who have coached this player see this. Use it when two players share a name
            (e.g. <span className="text-white">Rob Smith (Wirral)</span>) or to pin a note visible on their roster card.
          </p>
        </div>
        {!editing ? (
          <button onClick={startEdit} data-testid="coach-note-edit" className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> {hasAny ? "Edit" : "Add"}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(false)} data-testid="coach-note-cancel" className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors inline-flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button onClick={save} disabled={saving} data-testid="coach-note-save" className="btn-volt px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <KV label="Display-name override" value={u.display_name_override || "—"} />
          <KV label="Coach-only note" value={u.coach_note || "—"} />
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <div className="label-mono mb-1.5">Display-name override (shown to coaches instead of &quot;{u.name}&quot;)</div>
            <input
              type="text"
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              maxLength={120}
              placeholder={`e.g. ${u.name} (Wirral)`}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
              data-testid="coach-note-override-input"
            />
          </label>
          <label className="block">
            <div className="label-mono mb-1.5">Coach-only note (1000 chars max)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="e.g. Prefers evening sessions · Recovered from knee injury · Twin of the other Rob Smith"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00] leading-relaxed"
              data-testid="coach-note-note-input"
            />
            <div className="text-[10px] text-[#525252] mt-1 uppercase tracking-wider">{note.length} / 1000</div>
          </label>
        </div>
      )}
    </section>
  );
}


// Iter94 — Admin credit pack overrides. Renders one row per Store-bought pack
// with inline actions for extending expiry, adjusting used/total, and a
// free-form note captured in the audit log.
function CreditPackRow({ userId, pack, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [expiryDraft, setExpiryDraft] = useState(() => (pack.expires_at || "").slice(0, 10));
  const remaining = Math.max(0, Number(pack.sessions_total || 0) - Number(pack.sessions_used || 0));
  const expired = pack.expires_at && pack.expires_at.slice(0, 10) < new Date().toISOString().slice(0, 10);

  const apply = async (body, label) => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${userId}/credit-packs/${pack.id}`, body);
      toast.success(label);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#262626] p-3" data-testid={`credit-pack-${pack.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-semibold">
            {remaining} / {pack.sessions_total} left
            {expired && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#FF4500]">EXPIRED</span>}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5">
            {pack.status} · £{pack.price} · bought {(pack.created_at || "").slice(0, 10)} · expires {(pack.expires_at || "—").slice(0, 10)}
          </div>
        </div>
        <input
          type="date"
          value={expiryDraft}
          onChange={(e) => setExpiryDraft(e.target.value)}
          className="bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs text-white focus:border-[#CCFF00] focus:outline-none"
          data-testid={`credit-pack-expiry-${pack.id}`}
        />
        <button
          onClick={() => expiryDraft && apply({ expires_at: `${expiryDraft}T23:59:59+00:00` }, "Expiry updated")}
          disabled={busy || !expiryDraft}
          className="px-2.5 py-1 text-xs border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-50"
          data-testid={`credit-pack-save-expiry-${pack.id}`}
        >
          Save expiry
        </button>
        <button
          onClick={() => apply({ sessions_used_delta: -1, note: "admin gave 1 credit" }, "+1 credit added")}
          disabled={busy || Number(pack.sessions_used || 0) === 0}
          className="px-2.5 py-1 text-xs border border-[#262626] text-white hover:border-[#CCFF00] disabled:opacity-50"
          data-testid={`credit-pack-give-${pack.id}`}
        >
          +1 credit
        </button>
        <button
          onClick={() => apply({ sessions_total_delta: 1, note: "admin topped up pack" }, "+1 session added to pack")}
          disabled={busy}
          className="px-2.5 py-1 text-xs border border-[#262626] text-white hover:border-[#CCFF00] disabled:opacity-50"
          data-testid={`credit-pack-topup-${pack.id}`}
        >
          +1 total
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter107 — Gift Credits section. Admin-only, no Stripe leg. Creates a fresh
// generic pack the student can spend on any accept_credits-eligible session.
// Renders the existing pack list above it AND stays visible when the student
// has zero packs — the whole point is granting to fresh accounts too.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Iter108b — Admin-side coach visibility & Away Mode. Support can hide a
// coach from the public directory or toggle their Away flag without asking
// the coach to log in. Every write goes through /admin/users/{id}/coach-
// visibility which audit-logs the before/after snapshot.
// ---------------------------------------------------------------------------
function CoachVisibilityAdminSection({ user, onChanged }) {
  const [visible, setVisible] = useState(user.is_visible_to_public !== false);
  const [away, setAway] = useState(!!user.is_away);
  const [awayNote, setAwayNote] = useState(user.away_note || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setVisible(user.is_visible_to_public !== false);
    setAway(!!user.is_away);
    setAwayNote(user.away_note || "");
  }, [user.id, user.is_visible_to_public, user.is_away, user.away_note]);

  const patch = async (body, successMsg) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/users/${user.id}/coach-visibility`, body);
      setVisible(data.is_visible_to_public);
      setAway(data.is_away);
      setAwayNote(data.away_note || "");
      toast.success(successMsg);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="card-surface p-6 fade-up d3" data-testid="admin-coach-visibility-section">
      <div className="label-mono mb-1">
        {visible ? <Eye className="w-3.5 h-3.5 inline mr-1" style={{ color: "#CCFF00" }} /> : <EyeOff className="w-3.5 h-3.5 inline mr-1" style={{ color: "#FF4500" }} />}
        COACH VISIBILITY · ADMIN OVERRIDE
      </div>
      <h2 className="h-display text-2xl mb-1">Hide {user.name || "this coach"} from students, or mark them as away.</h2>
      <p className="text-sm text-[#A3A3A3] mb-5">Audit-logged. The coach can still flip these from their own profile — this is a fast-path for support.</p>

      {/* Directory visibility */}
      <div className="border border-[#262626] p-4 mb-3" data-testid="admin-visibility-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              {visible ? <Eye className="w-4 h-4 text-[#CCFF00]" /> : <EyeOff className="w-4 h-4 text-[#FF4500]" />}
              Directory visibility
              <span
                className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                style={visible ? { background: "#CCFF00", color: "#0A0A0A" } : { background: "#FF4500", color: "#0A0A0A" }}
              >
                {visible ? "Visible to players" : "Hidden from players"}
              </span>
            </div>
            <div className="text-xs text-[#A3A3A3] mt-1">
              {visible
                ? "Listed on /coaches, Coaches Near Me, and region avatars."
                : "Hidden from public lists. Existing students still see them."}
            </div>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => patch({ is_visible_to_public: !visible }, visible ? `${user.name || "Coach"} hidden from players` : `${user.name || "Coach"} back in directory`)}
            data-testid="admin-visibility-toggle"
            className="text-xs uppercase tracking-wider px-4 py-2 font-semibold disabled:opacity-50"
            style={visible
              ? { background: "transparent", color: "#FF4500", border: "1px solid #FF4500" }
              : { background: "#CCFF00", color: "#0A0A0A" }}
          >
            {visible ? "Hide from players" : "Show to players"}
          </button>
        </div>
      </div>

      {/* Away Mode */}
      <div className="border border-[#262626] p-4" data-testid="admin-away-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <Coffee className="w-4 h-4 text-[#FF9F1C]" /> Away Mode
              {away && <span className="ml-2 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#FF9F1C] text-black font-bold">Active</span>}
            </div>
            <div className="text-xs text-[#A3A3A3] mt-1">Stay listed with an Away badge, but disable the Book buttons.</div>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => patch({ is_away: !away, away_note: awayNote }, !away ? "Away mode ON — Book buttons paused" : "Away mode OFF — bookable again")}
            data-testid="admin-away-toggle"
            className="text-xs uppercase tracking-wider px-4 py-2 font-semibold disabled:opacity-50"
            style={away
              ? { background: "#FF9F1C", color: "#0A0A0A" }
              : { background: "transparent", color: "#FF9F1C", border: "1px solid #FF9F1C" }}
          >
            {away ? "Turn OFF" : "Turn ON"}
          </button>
        </div>
        <div className="mt-3">
          <div className="label-mono text-[10px] mb-1">Away note (optional · 200 char max)</div>
          <div className="flex gap-2">
            <input
              value={awayNote}
              maxLength={200}
              onChange={(e) => setAwayNote(e.target.value)}
              placeholder="Back from 14 Feb — travelling"
              data-testid="admin-away-note"
              className="flex-1 bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#FF9F1C]"
            />
            <button
              type="button"
              onClick={() => patch({ away_note: awayNote }, "Away note saved")}
              disabled={busy}
              data-testid="admin-away-note-save"
              className="text-xs uppercase tracking-wider px-3 py-2 border border-[#262626] hover:border-[#FF9F1C] hover:text-[#FF9F1C] disabled:opacity-50"
            >
              Save note
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Iter107 — Gift Credits section
// ---------------------------------------------------------------------------
function GiftCreditsSection({ userId, userName, onGranted }) {
  const defaultExpiry = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return d.toISOString().slice(0, 10);
  };
  const [count, setCount] = useState(1);
  const [expiry, setExpiry] = useState(defaultExpiry);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const n = Math.max(1, Math.min(100, Number(count) || 0));

  const openConfirm = () => {
    if (!n) {
      toast.error("Enter a number of credits (1-100)");
      return;
    }
    setConfirmOpen(true);
  };

  const grant = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/gift-credits`, {
        sessions_total: n,
        expires_at: expiry ? `${expiry}T23:59:59+00:00` : null,
        reason: reason || null,
      });
      toast.success(`Gifted ${n} credit${n === 1 ? "" : "s"} — valid until ${expiry}`);
      setReason("");
      setCount(1);
      setConfirmOpen(false);
      onGranted && onGranted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Grant failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="card-surface p-6 fade-up d4" data-testid="gift-credits-section">
      <div className="label-mono mb-1"><Gift className="w-3.5 h-3.5 inline mr-1" style={{ color: "#CCFF00" }} /> GIFT CREDITS</div>
      <h2 className="h-display text-2xl mb-1">Grant free session credits.</h2>
      <p className="text-sm text-[#A3A3A3] mb-4">Bypasses Stripe. Adds a new generic pack the student can spend on any coach&apos;s <span className="text-white">accept-credits</span> session. FIFO — earliest-expiring credits burn first.</p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="label-mono block mb-1">CREDITS</label>
          <input
            type="number"
            min={1} max={100}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-white focus:border-[#CCFF00] focus:outline-none"
            data-testid="gift-credits-count"
          />
        </div>
        <div>
          <label className="label-mono block mb-1">EXPIRES</label>
          <input
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-white focus:border-[#CCFF00] focus:outline-none"
            data-testid="gift-credits-expiry"
          />
        </div>
        <div className="md:col-span-2">
          <label className="label-mono block mb-1">REASON (audited · optional)</label>
          <input
            type="text"
            maxLength={400}
            placeholder="e.g. Goodwill after cancelled session"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-white focus:border-[#CCFF00] focus:outline-none"
            data-testid="gift-credits-reason"
          />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={openConfirm}
          disabled={busy}
          data-testid="gift-credits-submit"
          className="px-5 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
          style={{ background: "#CCFF00", color: "#0A0A0A" }}
        >
          <Gift className="w-3.5 h-3.5" /> {busy ? "Granting…" : `Gift ${n} credit${n === 1 ? "" : "s"}`}
        </button>
        <span className="text-[10px] uppercase tracking-wider text-[#525252]">Audit-logged · No payment taken</span>
      </div>

      {/* Iter108 — shadcn AlertDialog replaces the native window.confirm. Gives
          us a data-testid on the confirm button so automated tests can drive
          the full flow, and matches the visual language of the rest of the
          admin surface. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="gift-credits-confirm-dialog" className="bg-[#0A0A0A] border-[#262626] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Gift {n} credit{n === 1 ? "" : "s"} to {userName || "this student"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A3A3A3]">
              This creates a new generic pack — no payment is taken. Expires <span className="text-white">{expiry}</span>. The action is audit-logged with your name{reason ? " and reason" : ""}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="gift-credits-cancel"
              className="bg-transparent border border-[#262626] text-white hover:bg-[#141414] hover:text-white"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); grant(); }}
              disabled={busy}
              data-testid="gift-credits-confirm"
              className="text-black hover:opacity-90 disabled:opacity-50"
              style={{ background: "#CCFF00" }}
            >
              {busy ? "Granting…" : `Gift ${n} credit${n === 1 ? "" : "s"}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Iter94 — Credit packs section (existing store-bought packs)
// ---------------------------------------------------------------------------
function CreditPacksSection({ userId, packs, onChanged }) {
  return (
    <section className="card-surface p-6 fade-up d5" data-testid="user-credit-packs-section">
      <div className="label-mono mb-1">CREDIT PACKS · STORE-BOUGHT</div>
      <h2 className="h-display text-2xl mb-4">{packs.length} pack{packs.length === 1 ? "" : "s"}</h2>
      <div className="space-y-3">
        {packs.map((p) => (
          <CreditPackRow key={p.id} userId={userId} pack={p} onChanged={onChanged} />
        ))}
      </div>
    </section>
  );
}
